import { useCallback, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { InlineAlert } from '@chillout/ui'
import { RewardBalanceCard } from '../../shared/RewardBalanceCard'
import { AdjustmentForm } from './AdjustmentForm'
import { RewardAdjustConfirmModal } from './RewardAdjustConfirmModal'
import { useAdminAdjustRewards } from './useAdminAdjustMember'
import type { AdminMemberDetail } from './useAdminMemberDetail'

interface RewardsTabContentProps {
  detail: AdminMemberDetail
}

const MAX_REWARD_ADJUSTMENT = 20

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

function getCountLabel(delta: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const abs = Math.abs(delta)
  const label = abs === 1
    ? t('members.detail.rewardAdjustment.rewardCountSingle')
    : t('members.detail.rewardAdjustment.rewardCount', { count: abs })
  if (delta > 0) return `+ ${label}`
  if (delta < 0) return `- ${label}`
  return label
}


export function RewardsTabContent({ detail }: RewardsTabContentProps) {
  const { t, i18n } = useTranslation()
  const [selectedItemId, setSelectedItemId] = useState('')
  const [rewardDelta, setRewardDelta] = useState(0)
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const adjustMutation = useAdminAdjustRewards()
  const { rewardBalance, rewardCatalog } = detail

  const handleSelectItem = useCallback((itemId: string) => {
    setSelectedItemId((prev) => {
      if (prev === itemId) return prev
      setRewardDelta(0)
      return itemId
    })
  }, [])

  const selectedBalance = rewardBalance.find((r) => r.rewardItemId === selectedItemId)
  const selectedCatalogItem = rewardCatalog.find((c) => c.id === selectedItemId)
  const currentCount = selectedBalance?.count ?? 0
  const selectedRewardName = selectedBalance?.name ?? selectedCatalogItem?.name ?? ''
  const canApply = selectedItemId !== '' && rewardDelta !== 0 && reason !== ''

  const displayBalance = useMemo(() => {
    return rewardCatalog.map((catalogItem) => {
      const balance = rewardBalance.find((r) => r.rewardItemId === catalogItem.id)
      return {
        rewardItemId: catalogItem.id,
        name: catalogItem.name,
        count: balance?.count ?? 0,
        earliestExpiry: balance?.earliestExpiry ?? null,
        delta: catalogItem.id === selectedItemId ? rewardDelta : 0,
      }
    })
  }, [rewardBalance, rewardCatalog, selectedItemId, rewardDelta])

  return (
    <View className="gap-6">
      <View className="gap-3">
        <Text className="text-sm font-semibold text-primary">
          {t('members.detail.rewardAdjustment.rewardBalance')}
        </Text>
        {displayBalance.length > 0 ? (
          <View className="gap-4 lg:flex-row lg:flex-wrap">
            {displayBalance.map((r) => (
              <View key={r.rewardItemId} className="w-full lg:w-[calc(50%-8px)] 3xl:w-[calc(33.333%-11px)]">
                <RewardBalanceCard
                  name={r.name}
                  count={r.count}
                  delta={r.delta}
                  earliestExpiry={r.earliestExpiry}
                  language={i18n.language}
                  selected={r.rewardItemId === selectedItemId}
                  onPress={() => handleSelectItem(r.rewardItemId)}
                  accessibilityLabel={t('members.detail.rewardAdjustment.selectCardAccessibilityLabel', { name: r.name })}
                />
              </View>
            ))}
          </View>
        ) : (
          <Text className="text-sm font-normal text-muted">
            {t('members.detail.availableRewards.empty')}
          </Text>
        )}
      </View>

      <AdjustmentForm
        delta={rewardDelta}
        onDeltaChange={setRewardDelta}
        min={-currentCount}
        max={MAX_REWARD_ADJUSTMENT}
        reason={reason}
        onReasonChange={setReason}
        note={note}
        onNoteChange={setNote}
        canApply={canApply}
        onApply={() => {
          adjustMutation.reset()
          setShowConfirm(true)
        }}
        stepperLabel={t('members.detail.rewardAdjustment.adjustQuantity')}
        countLabel={getCountLabel(rewardDelta, t)}
        decrementAccessibilityLabel={t('members.detail.rewardAdjustment.decrementAccessibilityLabel')}
        incrementAccessibilityLabel={t('members.detail.rewardAdjustment.incrementAccessibilityLabel')}
        countAccessibilityLabel={t('members.detail.rewardAdjustment.countAccessibilityLabel', { count: rewardDelta })}
        reasonLabel={t('members.detail.rewardAdjustment.reason')}
        reasonPlaceholder={t('members.detail.rewardAdjustment.reasonPlaceholder')}
        noteLabel={t('members.detail.rewardAdjustment.note')}
        notePlaceholder={t('members.detail.rewardAdjustment.notePlaceholder')}
        applyLabel={t('members.detail.rewardAdjustment.applyButton')}
        disabled={selectedItemId === ''}
      />

      {adjustMutation.isError ? (
        <InlineAlert message={t('members.detail.rewardAdjustment.applyError')} />
      ) : null}

      <RewardAdjustConfirmModal
        visible={showConfirm}
        memberName={getFullName(detail.firstName, detail.lastName)}
        memberEmail={detail.email}
        rewardName={selectedRewardName}
        currentCount={currentCount}
        rewardDelta={rewardDelta}
        reason={reason}
        note={note}
        onConfirm={() => {
          adjustMutation.mutate(
            {
              memberId: detail.id,
              rewardItemId: selectedItemId,
              rewardsDelta: rewardDelta,
              correctionReason: reason,
              correctionNote: note,
            },
            {
              onSuccess: () => {
                setShowConfirm(false)
                setSelectedItemId('')
                setRewardDelta(0)
                setReason('')
                setNote('')
              },
            },
          )
        }}
        onCancel={() => setShowConfirm(false)}
      />
    </View>
  )
}